/**
 * Port Performance UTC datetime helpers.
 *
 * Tracker APIs often return port-local wall clocks mislabeled as `GMT`
 * (e.g. New Orleans LT `08:30` arrives as `... 08:30:00 GMT`). Those must be
 * converted to UTC using the port IANA timezone — not treated as true UTC.
 */

import { lookupPcPortTimeZone } from './pc-port-timezones.util';

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

export type PcWallClock = {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function padMs(ms: string): number {
  return Number((ms || '0').padEnd(3, '0').slice(0, 3));
}

function utcDate(
  y: number,
  mo: number,
  d: number,
  h = 0,
  mi = 0,
  s = 0,
  ms = 0,
): Date | null {
  const date = new Date(Date.UTC(y, mo, d, h, mi, s, ms));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Strip trailing timezone labels the backend attaches to wall clocks. */
function stripTzLabel(raw: string): string {
  return raw
    .replace(/[,\s]+GMT$/i, '')
    .replace(/[-\s]?UTC$/i, '')
    .replace(/\s+LT$/i, '')
    .trim();
}

/**
 * Parse wall-clock components from API date strings.
 * Ignores misleading `GMT` / `UTC` suffixes (port-local times).
 */
export function parsePcWallClock(
  value: string | number | Date | null | undefined,
): PcWallClock | null {
  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes(),
      second: value.getUTCSeconds(),
    };
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value === 0) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
      second: date.getUTCSeconds(),
    };
  }

  let raw = String(value).trim();
  if (!raw || raw === '-' || raw.toLowerCase() === 'null') {
    return null;
  }

  raw = stripTzLabel(raw);

  // RFC-like: Tue, 27 Jan 2026 08:30:00
  const rfc = raw.match(
    /^(?:[A-Za-z]{3},\s*)?(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (rfc) {
    const [, d, mon, y, h, mi, s = '0'] = rfc;
    const monthIdx = MONTH_INDEX[mon.toLowerCase()];
    if (monthIdx != null) {
      return {
        year: Number(y),
        month: monthIdx + 1,
        day: Number(d),
        hour: Number(h),
        minute: Number(mi),
        second: Number(s),
      };
    }
  }

  // ISO / SQL: YYYY-MM-DD[ T]HH:mm[:ss][.ffffff][Z|±hh:mm]
  const iso = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(?:Z|[+-]\d{2}:?\d{2})?$/i,
  );
  if (iso) {
    const [, y, mo, d, h, mi, s = '0'] = iso;
    return {
      year: Number(y),
      month: Number(mo),
      day: Number(d),
      hour: Number(h),
      minute: Number(mi),
      second: Number(s),
    };
  }

  // Date only: YYYY-MM-DD
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const [, y, mo, d] = dateOnly;
    return {
      year: Number(y),
      month: Number(mo),
      day: Number(d),
      hour: 0,
      minute: 0,
      second: 0,
    };
  }

  // DD-MMM-YYYY[ HH:mm[:ss]]
  const dmy = raw.match(
    /^(\d{1,2})[-\s]([A-Za-z]{3})[-\s](\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (dmy) {
    const [, d, mon, y, h = '0', mi = '0', s = '0'] = dmy;
    const monthIdx = MONTH_INDEX[mon.toLowerCase()];
    if (monthIdx != null) {
      return {
        year: Number(y),
        month: monthIdx + 1,
        day: Number(d),
        hour: Number(h),
        minute: Number(mi),
        second: Number(s),
      };
    }
  }

  // DD/MM/YYYY[ HH:mm[:ss]]
  const slash = raw.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (slash) {
    const [, d, mo, y, h = '0', mi = '0', s = '0'] = slash;
    return {
      year: Number(y),
      month: Number(mo),
      day: Number(d),
      hour: Number(h),
      minute: Number(mi),
      second: Number(s),
    };
  }

  return null;
}

/**
 * `Intl.DateTimeFormat` construction is expensive, and `readZoneParts` runs up
 * to four times per conversion — for every date cell, on every change-detection
 * pass. Reuse one formatter per timezone instead of rebuilding it each call.
 */
const ZONE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function getZoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = ZONE_FORMATTERS.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    ZONE_FORMATTERS.set(timeZone, formatter);
  }
  return formatter;
}

function readZoneParts(
  date: Date,
  timeZone: string,
): PcWallClock | null {
  try {
    const parts = getZoneFormatter(timeZone).formatToParts(date);

    const map: Record<string, string> = {};
    for (const part of parts) {
      if (part.type !== 'literal') {
        map[part.type] = part.value;
      }
    }

    const hourRaw = map['hour'] === '24' ? '0' : map['hour'];
    return {
      year: Number(map['year']),
      month: Number(map['month']),
      day: Number(map['day']),
      hour: Number(hourRaw),
      minute: Number(map['minute']),
      second: Number(map['second']),
    };
  } catch {
    return null;
  }
}

/**
 * Convert a wall clock that was observed in `timeZone` into a UTC Date.
 */
export function zonedWallClockToUtc(
  wall: PcWallClock,
  timeZone: string,
): Date | null {
  // Initial guess: treat wall as UTC, then correct against zone local parts.
  let utcMs = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second || 0,
  );

  for (let i = 0; i < 4; i++) {
    const asLocal = readZoneParts(new Date(utcMs), timeZone);
    if (!asLocal) {
      return null;
    }

    const desiredAsUtc = Date.UTC(
      wall.year,
      wall.month - 1,
      wall.day,
      wall.hour,
      wall.minute,
      wall.second || 0,
    );
    const actualAsUtc = Date.UTC(
      asLocal.year,
      asLocal.month - 1,
      asLocal.day,
      asLocal.hour,
      asLocal.minute,
      asLocal.second || 0,
    );
    const diff = desiredAsUtc - actualAsUtc;
    utcMs += diff;
    if (diff === 0) {
      break;
    }
  }

  const date = new Date(utcMs);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Parse a value as a true UTC instant (timezone-less strings are UTC). */
export function parsePcDateAsUtc(
  value: string | number | Date | null | undefined,
): Date | null {
  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value === 0) {
      return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  let raw = String(value).trim();
  if (!raw || raw === '-' || raw.toLowerCase() === 'null') {
    return null;
  }

  // Real timezone-aware strings (keep GMT here — may be true server UTC)
  if (/[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw) || /\bGMT$/i.test(raw)) {
    const aware = new Date(raw.includes('  ') ? raw.replace(/\s+/g, ' ') : raw);
    if (!Number.isNaN(aware.getTime())) {
      return aware;
    }
  }

  raw = stripTzLabel(raw);

  const isoLocal = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?$/,
  );
  if (isoLocal) {
    const [, y, mo, d, h, mi, s = '0', ms = '0'] = isoLocal;
    return utcDate(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s),
      padMs(ms),
    );
  }

  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const [, y, mo, d] = dateOnly;
    return utcDate(Number(y), Number(mo) - 1, Number(d));
  }

  const dmy = raw.match(
    /^(\d{1,2})[-\s]([A-Za-z]{3})[-\s](\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (dmy) {
    const [, d, mon, y, h = '0', mi = '0', s = '0'] = dmy;
    const monthIdx = MONTH_INDEX[mon.toLowerCase()];
    if (monthIdx != null) {
      return utcDate(
        Number(y),
        monthIdx,
        Number(d),
        Number(h),
        Number(mi),
        Number(s),
      );
    }
  }

  const slash = raw.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (slash) {
    const [, d, mo, y, h = '0', mi = '0', s = '0'] = slash;
    return utcDate(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s),
    );
  }

  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const forced = new Date(
    /[zZ]$/.test(normalized) || /[+-]\d{2}:?\d{2}$/.test(normalized)
      ? normalized
      : `${normalized}Z`,
  );
  if (!Number.isNaN(forced.getTime())) {
    return forced;
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatUtcWall(date: Date): string {
  const day = date.getUTCDate();
  const month = MONTHS_SHORT[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

/**
 * Table templates call the formatters below once per cell on every
 * change-detection pass, always with the same handful of inputs. Memoize on the
 * raw input so repeat renders are a map lookup instead of a full parse.
 *
 * `Date` inputs are keyed by epoch millis; anything else is keyed by its string
 * form, which is what the APIs actually return.
 */
const FORMAT_CACHE_LIMIT = 5000;

function cacheKeyFor(
  value: string | number | Date | null | undefined,
): string | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : `d:${time}`;
  }
  return `v:${String(value)}`;
}

function readThroughCache(
  cache: Map<string, string>,
  key: string | null,
  compute: () => string,
): string {
  if (key === null) {
    return compute();
  }
  const hit = cache.get(key);
  if (hit !== undefined) {
    return hit;
  }
  const result = compute();
  // Simple bound: the input space is the loaded dataset, so this only trips on
  // pathological data. Drop everything rather than track LRU ordering.
  if (cache.size >= FORMAT_CACHE_LIMIT) {
    cache.clear();
  }
  cache.set(key, result);
  return result;
}

const STANDARD_FORMAT_CACHE = new Map<string, string>();
const PORT_LOCAL_FORMAT_CACHE = new Map<string, string>();

/**
 * Standard UI date-time format used across frontend screens.
 * Example: `6 Sep 2026 01:00`
 */
export function formatStandardDateTime(
  value: string | number | Date | null | undefined,
): string {
  return readThroughCache(STANDARD_FORMAT_CACHE, cacheKeyFor(value), () => {
    const date = parsePcDateAsUtc(value);
    if (!date) {
      return '-';
    }
    return formatUtcWall(date);
  });
}

/**
 * Format as `15 Jul 2026 04:04` (UTC wall clock, no "UTC" suffix in the cell).
 * Use for true UTC fields (e.g. report_sent_date / updated_at).
 */
export function formatPcUtcDateTime(
  value: string | number | Date | null | undefined,
): string {
  return formatStandardDateTime(value);
}

/**
 * Format port-local arrival/departure/EOSP/COSP as UTC.
 * API values are local wall clocks (often mislabeled `GMT`).
 */
export function formatPcPortLocalAsUtc(
  value: string | number | Date | null | undefined,
  portName?: string | null,
  timeZone?: string | null,
): string {
  const tz = timeZone || lookupPcPortTimeZone(portName);
  const valueKey = cacheKeyFor(value);
  return readThroughCache(
    PORT_LOCAL_FORMAT_CACHE,
    valueKey === null ? null : `${tz ?? ''}|${valueKey}`,
    () => computePcPortLocalAsUtc(value, tz),
  );
}

function computePcPortLocalAsUtc(
  value: string | number | Date | null | undefined,
  tz: string | null,
): string {
  const wall = parsePcWallClock(value);
  if (!wall) {
    return '-';
  }

  if (!tz) {
    // No timezone → keep wall clock (legacy behavior) rather than invent an offset.
    const date = utcDate(
      wall.year,
      wall.month - 1,
      wall.day,
      wall.hour,
      wall.minute,
      wall.second,
    );
    return date ? formatUtcWall(date) : '-';
  }

  const utc = zonedWallClockToUtc(wall, tz);
  return utc ? formatUtcWall(utc) : '-';
}
